import React, { useState, useEffect } from 'react';
import { Project, ProjectConfig } from '../../types';
import { parseGraphML } from '../../lib/import/graphml';
import { CURRENCIES } from '../../config/currencies';
import { ExampleProjectLoader } from '../../lib/import/examples';
import { ProjectStore } from '../../lib/storage/projects';
import { URLManager } from '../../lib/url/URLManager';
import { updateStationPopulation } from '../../utils/population';
import { hasSkipFlag, resetSkipFlag } from '../../lib/storage/exampleSkip';

interface Props {
  project: Project;
  onProjectChange: (project: Project) => void;
  onDeleteProject: () => void;
  onDuplicateProject: () => void;
  onDeleteAllProjects: () => Promise<void>;
  projectStore: ProjectStore;
}

export const ConfigForm: React.FC<Props> = ({
  project,
  onProjectChange,
  onDeleteProject,
  onDuplicateProject,
  onDeleteAllProjects,
  projectStore
}) => {
  type InputField = keyof typeof inputValues;
  
  const formatNumber = (value: number, decimals: number = 0): string => {
    return value.toLocaleString('en-US', { 
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const parseNumber = (value: string): number => {
    // Remove thousand separators and parse
    return parseFloat(value.replace(/,/g, ''));
  };

  // Local state for all numeric inputs
  const [inputValues, setInputValues] = React.useState({
    calibrationConstant: formatNumber(project.config.calculation?.calibrationConstant ?? 200000),
    calibrationConstantCustoms: formatNumber(project.config.calculation?.calibrationConstantCustoms ?? 20000),
    calibrationConstantNoCustoms: formatNumber(project.config.calculation?.calibrationConstantNoCustoms ?? 30000),
    densityFrictionFactor: formatNumber(project.config.calculation?.densityFrictionFactor ?? 0.80, 2),
    distanceFrictionFactor: formatNumber(project.config.calculation?.distanceFrictionFactor ?? 2.00, 2),
    minimumRouteDistance: formatNumber(project.config.calculation?.minimumRouteDistance ?? 500),
    populationRadius: formatNumber(project.config.calculation?.populationRadius ?? 30),
    duration: formatNumber(project.config.bond?.duration ?? 50),
    interestRate: formatNumber(project.config.bond?.interestRate ?? 3.00, 2),
    constructionCostPerKm: formatNumber(project.config.costs?.constructionCostPerKm ?? 25000000),
    operatingCostPerKm: formatNumber(project.config.costs?.operatingCostPerKm ?? 0.09, 2),
    farePerKm: formatNumber(project.config.costs?.farePerKm ?? 0.15, 2),
    businessShareOfSeats: formatNumber(project.config.costs?.businessShareOfSeats ?? 20),
    businessTicketPremium: formatNumber(project.config.costs?.businessTicketPremium ?? 40),
    discountShareOfSeats: formatNumber(project.config.costs?.discountShareOfSeats ?? 20),
    discountTicketReduction: formatNumber(project.config.costs?.discountTicketReduction ?? 40),
    seatsPerTrain: formatNumber(project.config.trains?.seatsPerTrain ?? 400),
    loadFactor: formatNumber(project.config.trains?.loadFactor ?? 75),
    maxSpeed: formatNumber(project.config.trains?.maxSpeed ?? 300),
    minimumStationDwellTime: formatNumber(project.config.trains?.minimumStationDwellTime ?? 2),
    maximumStationDwellTime: formatNumber(project.config.trains?.maximumStationDwellTime ?? 5),
    minimumHeadway: formatNumber(project.config.trains?.minimumHeadway ?? 3)
  });

  const [selectedFileName, setSelectedFileName] = React.useState<string>('No file chosen');

  const [projectName, setProjectName] = React.useState(project.name);
  const [identifierInput, setIdentifierInput] = React.useState(project.identifier || '');
  const [isRecalculating, setIsRecalculating] = React.useState(false);
  const [needsRecalculation, setNeedsRecalculation] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSkipFlag, setShowSkipFlag] = useState(false);

  // Check if any stations have a different radius than the project config
  React.useEffect(() => {
    const stationsNeedingUpdate = project.network.stations.filter(
      station => 
        station.latitude !== undefined && 
        station.latitude !== null && 
        station.longitude !== undefined && 
        station.longitude !== null && 
        station.circle_population_radius !== project.config.calculation.populationRadius
    );
    if (stationsNeedingUpdate.length > 0) {
      console.log(`${stationsNeedingUpdate.length} stations need population recalculation`);
    }
    setNeedsRecalculation(stationsNeedingUpdate.length > 0);
  }, [project.network.stations, project.config.calculation.populationRadius]);

  // Check for skip flag on mount and when project changes
  useEffect(() => {
    setShowSkipFlag(hasSkipFlag());
  }, [project]);

  // Update local state when project changes
  React.useEffect(() => {
    setInputValues({
      calibrationConstant: formatNumber(project.config.calculation?.calibrationConstant ?? 200000),
      calibrationConstantCustoms: formatNumber(project.config.calculation?.calibrationConstantCustoms ?? 20000),
      calibrationConstantNoCustoms: formatNumber(project.config.calculation?.calibrationConstantNoCustoms ?? 30000),
      densityFrictionFactor: formatNumber(project.config.calculation?.densityFrictionFactor ?? 0.80, 2),
      distanceFrictionFactor: formatNumber(project.config.calculation?.distanceFrictionFactor ?? 2.00, 2),
      minimumRouteDistance: formatNumber(project.config.calculation?.minimumRouteDistance ?? 500),
      populationRadius: formatNumber(project.config.calculation?.populationRadius ?? 30),
      duration: formatNumber(project.config.bond?.duration ?? 50),
      interestRate: formatNumber(project.config.bond?.interestRate ?? 3.00, 2),
      constructionCostPerKm: formatNumber(project.config.costs?.constructionCostPerKm ?? 25000000),
      operatingCostPerKm: formatNumber(project.config.costs?.operatingCostPerKm ?? 0.09, 2),
      farePerKm: formatNumber(project.config.costs?.farePerKm ?? 0.15, 2),
      businessShareOfSeats: formatNumber(project.config.costs?.businessShareOfSeats ?? 20),
      businessTicketPremium: formatNumber(project.config.costs?.businessTicketPremium ?? 40),
      discountShareOfSeats: formatNumber(project.config.costs?.discountShareOfSeats ?? 20),
      discountTicketReduction: formatNumber(project.config.costs?.discountTicketReduction ?? 40),
      seatsPerTrain: formatNumber(project.config.trains?.seatsPerTrain ?? 400),
      loadFactor: formatNumber(project.config.trains?.loadFactor ?? 75),
      maxSpeed: formatNumber(project.config.trains?.maxSpeed ?? 300),
      minimumStationDwellTime: formatNumber(project.config.trains?.minimumStationDwellTime ?? 2),
      maximumStationDwellTime: formatNumber(project.config.trains?.maximumStationDwellTime ?? 5),
      minimumHeadway: formatNumber(project.config.trains?.minimumHeadway ?? 3)
    });
    setProjectName(project.name);
    setIdentifierInput(project.identifier || '');
  }, [project]);

  const handleInputChange = (field: InputField, value: string) => {
    setInputValues(prev => ({ ...prev, [field]: value }));
  };

  const handleInputBlur = (
    section: keyof ProjectConfig,
    field: InputField,
    decimals: number = 0
  ) => {
    // Remove thousand separators before parsing
    const parsed = parseNumber(inputValues[field]);
    if (!isNaN(parsed) && parsed >= 0) {
      const rounded = Number(parsed.toFixed(decimals));
      // Update project state first
      handleConfigChange(section, field, rounded);
      // Update local state with formatted value
      setInputValues(prev => ({ ...prev, [field]: formatNumber(rounded, decimals) }));
    } else {
      // Revert to the last valid value from the project and format with thousand separators
      const currentValue = project.config[section][field as keyof typeof project.config[typeof section]] as number;
      setInputValues(prev => ({ ...prev, [field]: formatNumber(currentValue, decimals) }));
    }
  };

  const handleConfigChange = async (
    section: keyof ProjectConfig | 'identifier',
    field: string,
    value: string | number
  ) => {
    if (section === 'identifier') {
      const newIdentifier = value as string;
      const newProject = {
        ...project,
        identifier: newIdentifier
      };
      onProjectChange(newProject);
      return;
    }

    const newProject = {
      ...project,
      config: {
        ...project.config,
        [section]: {
          ...project.config[section],
          [field]: value
        }
      }
    };
    onProjectChange(newProject);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProjectName(event.target.value);
  };

  const handleNameBlur = () => {
    // Only update if the name is not empty
    if (projectName.trim()) {
      const newProject = {
        ...project,
        name: projectName.trim()
      };
      onProjectChange(newProject);
    } else {
      // Revert to the last valid name if empty
      setProjectName(project.name);
    }
  };

  const handleIdentifierChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIdentifierInput(event.target.value);
  };

  const handleIdentifierBlur = async () => {
    // Skip validation if the identifier hasn't changed
    if (identifierInput === project.identifier) {
      return;
    }

    // Check if the new identifier is reserved
    if (URLManager.getInstance().isReservedIdentifier(identifierInput)) {
      alert('This identifier is reserved and cannot be used. Please choose a different one.');
      // Revert to the previous identifier
      setIdentifierInput(project.identifier || '');
      return;
    }

    // Check if the new identifier is unique
    const isUnique = await projectStore.isIdentifierUnique(identifierInput);
    if (!isUnique) {
      alert('This project identifier is already in use. Please choose a different one.');
      // Revert to the previous identifier
      setIdentifierInput(project.identifier || '');
      return;
    }

    // If we get here, the identifier is valid, so update the project
    const newProject = {
      ...project,
      identifier: identifierInput
    };
    onProjectChange(newProject);
  };

  const handleDeleteClick = () => {
    if (confirm('Are you sure you want to delete this project?')) {
      onDeleteProject();
    }
  };

  const handleRecalculateClick = async () => {
    const stations = project.network.stations.filter(
      station => 
        station.latitude !== undefined && 
        station.latitude !== null && 
        station.longitude !== undefined && 
        station.longitude !== null && 
        station.circle_population_radius !== project.config.calculation.populationRadius
    );

    const message = stations.length > 50
      ? `This will update ${stations.length} cities to use the new population radius. This may take longer than 1 minute. This cannot be undone. Continue?`
      : `This will update ${stations.length} cities to use the new population radius. This cannot be undone. Continue?`;

    if (!confirm(message)) {
      return;
    }

    setIsRecalculating(true);
    const startTime = Date.now();
    let successCount = 0;
    let failureCount = 0;
    let skipCount = 0;
    let lastFailedCity: string | null = null;

    try {
      const updatedStations = [...project.network.stations];
      let updatedCount = 0;

      for (const station of stations) {
        const oldRadius = station.circle_population_radius;
        const oldPopulation = station.circle_population;
        const newRadius = project.config.calculation.populationRadius;

        try {
          const updatedStation = await updateStationPopulation(
            { ...station, circle_population_radius: newRadius },
            project.config
          );

          // If the station is unchanged, it means the API call failed
          if (updatedStation === station) {
            failureCount++;
            lastFailedCity = station.name;
            console.log(
              `Station "${station.name}" update failed:\n` +
              `  Coordinates: ${station.latitude}, ${station.longitude}\n` +
              `  Radius: ${oldRadius}km → ${newRadius}km\n` +
              `  Population: ${oldPopulation?.toLocaleString()} (unchanged)\n` +
              `  API: failed\n` +
              `  Save: skipped`
            );
            continue;
          }

          if (updatedStation.circle_population !== station.circle_population) {
            const index = updatedStations.findIndex(s => s.id === station.id);
            if (index !== -1) {
              updatedStations[index] = updatedStation;
              updatedCount++;
              successCount++;
              console.log(
                `Station "${station.name}" updated successfully:\n` +
                `  Coordinates: ${station.latitude}, ${station.longitude}\n` +
                `  Radius: ${oldRadius}km → ${newRadius}km\n` +
                `  Population: ${oldPopulation?.toLocaleString()} → ${updatedStation.circle_population?.toLocaleString()}\n` +
                `  API: success\n` +
                `  Save: success`
              );
            } else {
              failureCount++;
              lastFailedCity = station.name;
              console.log(
                `Station "${station.name}" update failed:\n` +
                `  Coordinates: ${station.latitude}, ${station.longitude}\n` +
                `  Radius: ${oldRadius}km → ${newRadius}km\n` +
                `  Population: ${oldPopulation?.toLocaleString()} (unchanged)\n` +
                `  API: success\n` +
                `  Save: failed (station not found in array)`
              );
            }
          } else {
            skipCount++;
            console.log(
              `Station "${station.name}" skipped:\n` +
              `  Coordinates: ${station.latitude}, ${station.longitude}\n` +
              `  Radius: ${oldRadius}km → ${newRadius}km\n` +
              `  Population: ${oldPopulation?.toLocaleString()} (unchanged)\n` +
              `  API: success\n` +
              `  Save: skipped (no population change)`
            );
          }
        } catch (error) {
          failureCount++;
          lastFailedCity = station.name;
          console.log(
            `Station "${station.name}" update failed:\n` +
            `  Coordinates: ${station.latitude}, ${station.longitude}\n` +
            `  Radius: ${oldRadius}km → ${newRadius}km\n` +
            `  Population: ${oldPopulation?.toLocaleString()} (unchanged)\n` +
            `  API: failed (${error})\n` +
            `  Save: skipped`
          );
        }

        // Wait 25ms between API calls
        await new Promise(resolve => setTimeout(resolve, 10));
      }

      if (updatedCount > 0) {
        const newProject = {
          ...project,
          network: {
            ...project.network,
            stations: updatedStations
          }
        };
        onProjectChange(newProject);
      }

      const duration = (Date.now() - startTime) / 1000;
      console.log(
        `Population recalculation completed:\n` +
        `  Duration: ${duration.toFixed(1)}s\n` +
        `  Total stations: ${stations.length}\n` +
        `  Succeeded: ${successCount}\n` +
        `  Failed: ${failureCount}\n` +
        `  Skipped: ${skipCount}\n` +
        `  Average time per station: ${(duration / stations.length).toFixed(1)}s`
      );

      if (failureCount > 0) {
        alert(
          `Recalculation completed with some failures:\n\n` +
          `${successCount} ${successCount === 1 ? 'city' : 'cities'} updated successfully\n` +
          `${failureCount} ${failureCount === 1 ? 'city' : 'cities'} failed to update\n` +
          `${skipCount} ${skipCount === 1 ? 'city' : 'cities'} skipped (no population change)\n\n` +
          (failureCount === 1
            ? `The population update failed for ${lastFailedCity}.`
            : `Use the browser console for details about which cities failed.`)
        );
      }
    } catch (error) {
      console.error('Failed to complete population recalculation:', error);
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const { network, config, projectName, identifier } = await parseGraphML(file);

      // Create new project with identifier from GraphML if available
      const newProject = {
        ...project,
        lastEdited: Date.now(),
        network,
        viewState: undefined, // Reset view state to ensure proper centering
        ...(config && { config }), // Only update config if it was found in the file
        ...(projectName && { name: projectName }), // Only update name if it was found in the file
        // Always use identifier from GraphML if available, regardless of current project's identifier
        ...(identifier && { identifier })
      };

      onProjectChange(newProject);

      // Update project name if it was found in the file
      if (projectName) {
        setProjectName(projectName);
      }
    } catch (error) {
      console.error('Failed to import GraphML:', { error, fileName: file.name });
      alert('Failed to import GraphML file. Please make sure the file is valid.');
    }

    // Reset the input
    event.target.value = '';
  };

  return (
    <div className="px-3 pt-3 sm:pt-6 md:pt-8 pb-24 max-w-2xl mx-auto">
      <div className="space-y-8">
        {/* Project Name */}
        <div>
          <h2 className="text-lg font-medium text-[#4A4538] mb-4">
            <div className="flex items-center gap-1">
              <span>Project Name</span>
            </div>
          </h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="projectName" className="block text-[#4A4538] mb-1">
                Project Name
              </label>
              <input
                id="projectName"
                type="text"
                value={projectName}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                className="w-full p-2 border rounded bg-white text-[#4A4538]"
              />
            </div>
            <div>
              <label htmlFor="projectIdentifier" className="block text-[#4A4538] mb-1">
                Project Identifier
              </label>
              <input
                id="projectIdentifier"
                type="text"
                value={identifierInput}
                onChange={handleIdentifierChange}
                onBlur={handleIdentifierBlur}
                className="w-full p-2 border rounded bg-white text-[#4A4538]"
              />
            </div>
          </div>
        </div>

        {/* Bond Settings */}
        <div>
          <h2 className="text-lg font-medium text-[#4A4538] mb-4">
            <div className="flex items-center gap-1">
              <span>Construction Cost</span>
            </div>
          </h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="constructionCostPerKm" className="block text-[#4A4538] mb-1">
                Construction Cost per Kilometer (<a href="https://transitcosts.com/high-speed-rail-preliminary-data-analysis/" target="_blank" rel="noopener noreferrer" className="text-[#6A8CAF] hover:text-[#4A4538]">examples</a>)
              </label>
              <div className="relative">
                <input
                  id="constructionCostPerKm"
                  type="text"
                  inputMode="numeric"
                  name="constructionCostPerKm"
                  value={inputValues.constructionCostPerKm}
                  onChange={(e) => handleInputChange('constructionCostPerKm', e.target.value)}
                  onBlur={(e) => handleInputBlur('costs', e.target.name as InputField)}
                  className="w-full p-2 border rounded bg-white text-[#4A4538] pr-12"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  {project.config.bond.currency}
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="duration" className="block text-[#4A4538] mb-1">
                Loan Duration
              </label>
              <div className="relative">
                <input
                  id="duration"
                  type="text"
                  inputMode="numeric"
                  name="duration"
                  value={inputValues.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  onBlur={(e) => handleInputBlur('bond', e.target.name as InputField)}
                  className="w-full p-2 border rounded bg-white text-[#4A4538] pr-16"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  years
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="interestRate" className="block text-[#4A4538] mb-1">
                Annual Interest Rate (<a href="https://www.worldgovernmentbonds.com/" target="_blank" rel="noopener noreferrer" className="text-[#6A8CAF] hover:text-[#4A4538]">examples</a>)
              </label>
              <div className="relative">
                <input
                  id="interestRate"
                  type="text"
                  inputMode="decimal"
                  name="interestRate"
                  value={inputValues.interestRate}
                  onChange={(e) => handleInputChange('interestRate', e.target.value)}
                  onBlur={(e) => handleInputBlur('bond', e.target.name as InputField, 2)}
                  className="w-full p-2 border rounded bg-white text-[#4A4538] pr-10"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  %
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="currency" className="block text-[#4A4538] mb-1">
                Currency
              </label>
              <div className="relative">
                <select
                  id="currency"
                  value={project.config.bond.currency}
                  onChange={(e) => handleConfigChange('bond', 'currency', e.target.value)}
                  className="w-full p-2 border rounded bg-white text-[#4A4538] appearance-none"
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  {Object.entries(CURRENCIES).map(([code, config]) => (
                    <option key={code} value={code}>
                      {config.displayName}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ticket Price Settings */}
        <div>
          <h2 className="text-lg font-medium text-[#4A4538] mb-4">
            <div className="flex items-center gap-1">
              <span>Ticket Price</span>
            </div>
          </h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="farePerKm" className="block text-[#4A4538] mb-1">
                Average Fare per Passenger Kilometer (<a href="https://pedestrianobservations.com/2024/05/17/fares-on-high-speed-trains/" target="_blank" rel="noopener noreferrer" className="text-[#6A8CAF] hover:text-[#4A4538]">example</a>)
              </label>
              <div className="relative">
                <input
                  id="farePerKm"
                  type="text"
                  inputMode="decimal"
                  name="farePerKm"
                  value={inputValues.farePerKm}
                  onChange={(e) => handleInputChange('farePerKm', e.target.value)}
                  onBlur={(e) => handleInputBlur('costs', e.target.name as InputField, 2)}
                  className="w-full p-2 border rounded bg-white text-[#4A4538] pr-12"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  {project.config.bond.currency}
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="operatingCostPerKm" className="block text-[#4A4538] mb-1">
                Operating Cost per Passenger Kilometer (<a href="https://pedestrianobservations.com/2018/02/19/what-the-spinetta-axe-reveals-about-cost-control/" target="_blank" rel="noopener noreferrer" className="text-[#6A8CAF] hover:text-[#4A4538]">example</a>)
              </label>
              <div className="relative">
                <input
                  id="operatingCostPerKm"
                  type="text"
                  inputMode="decimal"
                  name="operatingCostPerKm"
                  value={inputValues.operatingCostPerKm}
                  onChange={(e) => handleInputChange('operatingCostPerKm', e.target.value)}
                  onBlur={(e) => handleInputBlur('costs', e.target.name as InputField, 2)}
                  className="w-full p-2 border rounded bg-white text-[#4A4538] pr-12"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  {project.config.bond.currency}
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label htmlFor="businessShareOfSeats" className="block text-[#4A4538] mb-1">
                  Business share of seats
                </label>
                <div className="relative">
                  <input
                    id="businessShareOfSeats"
                    type="text"
                    inputMode="numeric"
                    name="businessShareOfSeats"
                    value={inputValues.businessShareOfSeats}
                    onChange={(e) => handleInputChange('businessShareOfSeats', e.target.value)}
                    onBlur={(e) => handleInputBlur('costs', e.target.name as InputField)}
                    className="w-full p-2 border rounded bg-white text-[#4A4538] pr-10"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    %
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <label htmlFor="businessTicketPremium" className="block text-[#4A4538] mb-1">
                  Business ticket price premium
                </label>
                <div className="relative">
                  <input
                    id="businessTicketPremium"
                    type="text"
                    inputMode="numeric"
                    name="businessTicketPremium"
                    value={inputValues.businessTicketPremium}
                    onChange={(e) => handleInputChange('businessTicketPremium', e.target.value)}
                    onBlur={(e) => handleInputBlur('costs', e.target.name as InputField)}
                    className="w-full p-2 border rounded bg-white text-[#4A4538] pr-10"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    %
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label htmlFor="discountShareOfSeats" className="block text-[#4A4538] mb-1">
                  Discount share of seats
                </label>
                <div className="relative">
                  <input
                    id="discountShareOfSeats"
                    type="text"
                    inputMode="numeric"
                    name="discountShareOfSeats"
                    value={inputValues.discountShareOfSeats}
                    onChange={(e) => handleInputChange('discountShareOfSeats', e.target.value)}
                    onBlur={(e) => handleInputBlur('costs', e.target.name as InputField)}
                    className="w-full p-2 border rounded bg-white text-[#4A4538] pr-10"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    %
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <label htmlFor="discountTicketReduction" className="block text-[#4A4538] mb-1">
                  Discount ticket price reduction
                </label>
                <div className="relative">
                  <input
                    id="discountTicketReduction"
                    type="text"
                    inputMode="numeric"
                    name="discountTicketReduction"
                    value={inputValues.discountTicketReduction}
                    onChange={(e) => handleInputChange('discountTicketReduction', e.target.value)}
                    onBlur={(e) => handleInputBlur('costs', e.target.name as InputField)}
                    className="w-full p-2 border rounded bg-white text-[#4A4538] pr-10"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    %
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trains Settings */}
        <div>
          <h2 className="text-lg font-medium text-[#4A4538] mb-4">
            <div className="flex items-center gap-1">
              <span>Trains</span>
            </div>
          </h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="seatsPerTrain" className="block text-[#4A4538] mb-1">
                Number of seats per train
              </label>
              <input
                id="seatsPerTrain"
                type="text"
                inputMode="numeric"
                name="seatsPerTrain"
                value={inputValues.seatsPerTrain}
                onChange={(e) => handleInputChange('seatsPerTrain', e.target.value)}
                onBlur={(e) => handleInputBlur('trains', e.target.name as InputField)}
                className="w-full p-2 border rounded bg-white text-[#4A4538]"
              />
            </div>
            <div>
              <label htmlFor="loadFactor" className="block text-[#4A4538] mb-1">
                Load factor
              </label>
              <div className="relative">
                <input
                  id="loadFactor"
                  type="text"
                  inputMode="numeric"
                  name="loadFactor"
                  value={inputValues.loadFactor}
                  onChange={(e) => handleInputChange('loadFactor', e.target.value)}
                  onBlur={(e) => handleInputBlur('trains', e.target.name as InputField)}
                  className="w-full p-2 border rounded bg-white text-[#4A4538] pr-10"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  %
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="maxSpeed" className="block text-[#4A4538] mb-1">
                Maximum speed
              </label>
              <div className="relative">
                <input
                  id="maxSpeed"
                  type="text"
                  inputMode="numeric"
                  name="maxSpeed"
                  value={inputValues.maxSpeed}
                  onChange={(e) => handleInputChange('maxSpeed', e.target.value)}
                  onBlur={(e) => {
                    const value = parseNumber(e.target.value);
                    if (isNaN(value)) {
                      // If invalid, revert to previous value
                      setInputValues(prev => ({ ...prev, maxSpeed: formatNumber(project.config.trains.maxSpeed) }));
                      return;
                    }
                    // Clamp between 1 and 1000
                    const clampedValue = Math.max(1, Math.min(1000, Math.round(value)));
                    // Update the input value with the clamped value
                    setInputValues(prev => ({ ...prev, maxSpeed: formatNumber(clampedValue) }));
                    // Save the clamped value to the project config
                    handleConfigChange('trains', 'maxSpeed', clampedValue);
                  }}
                  className="w-full p-2 border rounded bg-white text-[#4A4538] pr-12"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  km/h
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="minimumHeadway" className="block text-[#4A4538] mb-1">
                Minimum headway
              </label>
              <div className="relative">
                <input
                  id="minimumHeadway"
                  type="text"
                  inputMode="numeric"
                  name="minimumHeadway"
                  value={inputValues.minimumHeadway}
                  onChange={(e) => handleInputChange('minimumHeadway', e.target.value)}
                  onBlur={(e) => {
                    const value = parseNumber(e.target.value);
                    if (isNaN(value)) {
                      // If invalid, revert to previous value
                      setInputValues(prev => ({ ...prev, minimumHeadway: formatNumber(project.config.trains.minimumHeadway) }));
                      return;
                    }
                    // Clamp between 1 and 30
                    const clampedValue = Math.max(1, Math.min(30, Math.round(value)));
                    // Update the input value with the clamped value
                    setInputValues(prev => ({ ...prev, minimumHeadway: formatNumber(clampedValue) }));
                    // Save the clamped value to the project config
                    handleConfigChange('trains', 'minimumHeadway', clampedValue);
                  }}
                  className="w-full p-2 border rounded bg-white text-[#4A4538] pr-12"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  min
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label htmlFor="minimumStationDwellTime" className="block text-[#4A4538] mb-1">
                  Minimum station dwell time
                </label>
                <div className="relative">
                  <input
                    id="minimumStationDwellTime"
                    type="text"
                    inputMode="numeric"
                    name="minimumStationDwellTime"
                    value={inputValues.minimumStationDwellTime}
                    onChange={(e) => handleInputChange('minimumStationDwellTime', e.target.value)}
                    onBlur={(e) => handleInputBlur('trains', e.target.name as InputField)}
                    className="w-full p-2 border rounded bg-white text-[#4A4538] pr-12"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    min
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <label htmlFor="maximumStationDwellTime" className="block text-[#4A4538] mb-1">
                  Maximum station dwell time
                </label>
                <div className="relative">
                  <input
                    id="maximumStationDwellTime"
                    type="text"
                    inputMode="numeric"
                    name="maximumStationDwellTime"
                    value={inputValues.maximumStationDwellTime}
                    onChange={(e) => handleInputChange('maximumStationDwellTime', e.target.value)}
                    onBlur={(e) => handleInputBlur('trains', e.target.name as InputField)}
                    className="w-full p-2 border rounded bg-white text-[#4A4538] pr-12"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    min
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Calculation Settings */}
        <div>
          <h2 className="text-lg font-medium text-[#4A4538] mb-4">
            <div className="flex items-center gap-1">
              <span>Gravity Calculation</span>
            </div>
          </h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="calibrationConstant" className="block text-[#4A4538] mb-1">
                Calibration Constant: No border crossing
              </label>
              <input
                id="calibrationConstant"
                type="text"
                inputMode="numeric"
                name="calibrationConstant"
                value={inputValues.calibrationConstant}
                onChange={(e) => handleInputChange('calibrationConstant', e.target.value)}
                onBlur={(e) => handleInputBlur('calculation', e.target.name as InputField)}
                className="w-full p-2 border rounded bg-white text-[#4A4538]"
              />
            </div>
            <div>
              <label htmlFor="calibrationConstantCustoms" className="block text-[#4A4538] mb-1">
                Calibration Constant: Border crossing with customs
              </label>
              <input
                id="calibrationConstantCustoms"
                type="text"
                inputMode="numeric"
                name="calibrationConstantCustoms"
                value={inputValues.calibrationConstantCustoms}
                onChange={(e) => handleInputChange('calibrationConstantCustoms', e.target.value)}
                onBlur={(e) => handleInputBlur('calculation', e.target.name as InputField)}
                className="w-full p-2 border rounded bg-white text-[#4A4538]"
              />
            </div>
            <div>
              <label htmlFor="calibrationConstantNoCustoms" className="block text-[#4A4538] mb-1">
                Calibration Constant: Border crossing without customs
              </label>
              <input
                id="calibrationConstantNoCustoms"
                type="text"
                inputMode="numeric"
                name="calibrationConstantNoCustoms"
                value={inputValues.calibrationConstantNoCustoms}
                onChange={(e) => handleInputChange('calibrationConstantNoCustoms', e.target.value)}
                onBlur={(e) => handleInputBlur('calculation', e.target.name as InputField)}
                className="w-full p-2 border rounded bg-white text-[#4A4538]"
              />
            </div>
            <div>
              <label htmlFor="densityFrictionFactor" className="block text-[#4A4538] mb-1">
                Density Friction Factor
              </label>
              <input
                id="densityFrictionFactor"
                type="text"
                inputMode="decimal"
                name="densityFrictionFactor"
                value={inputValues.densityFrictionFactor}
                onChange={(e) => handleInputChange('densityFrictionFactor', e.target.value)}
                onBlur={(e) => {
                  const value = parseNumber(e.target.value);
                  if (isNaN(value)) {
                    // If invalid, revert to previous value
                    setInputValues(prev => ({ ...prev, densityFrictionFactor: formatNumber(project.config.calculation.densityFrictionFactor, 2) }));
                    return;
                  }
                  // Clamp between 0.10 and 2.00
                  const clampedValue = Math.max(0.10, Math.min(2.00, Number(value.toFixed(2))));
                  // Update the input value with the clamped value
                  setInputValues(prev => ({ ...prev, densityFrictionFactor: formatNumber(clampedValue, 2) }));
                  // Save the clamped value to the project config
                  handleConfigChange('calculation', 'densityFrictionFactor', clampedValue);
                }}
                className="w-full p-2 border rounded bg-white text-[#4A4538]"
              />
            </div>
            <div>
              <label htmlFor="distanceFrictionFactor" className="block text-[#4A4538] mb-1">
                Distance Friction Factor
              </label>
              <input
                id="distanceFrictionFactor"
                type="text"
                inputMode="decimal"
                name="distanceFrictionFactor"
                value={inputValues.distanceFrictionFactor}
                onChange={(e) => handleInputChange('distanceFrictionFactor', e.target.value)}
                onBlur={(e) => handleInputBlur('calculation', e.target.name as InputField, 2)}
                className="w-full p-2 border rounded bg-white text-[#4A4538]"
              />
            </div>
            <div>
              <label htmlFor="minimumRouteDistance" className="block text-[#4A4538] mb-1">
                Minimum Route Distance
              </label>
              <div className="relative">
                <input
                  id="minimumRouteDistance"
                  type="text"
                  inputMode="numeric"
                  name="minimumRouteDistance"
                  value={inputValues.minimumRouteDistance}
                  onChange={(e) => handleInputChange('minimumRouteDistance', e.target.value)}
                  onBlur={(e) => handleInputBlur('calculation', e.target.name as InputField)}
                  className="w-full p-2 border rounded bg-white text-[#4A4538] pr-12"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  km
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="populationRadius" className="block text-[#4A4538] mb-1">
                Population Radius
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    id="populationRadius"
                    type="text"
                    inputMode="numeric"
                    name="populationRadius"
                    value={inputValues.populationRadius}
                    onChange={(e) => handleInputChange('populationRadius', e.target.value)}
                    onBlur={(e) => {
                      const value = parseNumber(e.target.value);
                      if (isNaN(value)) {
                        // If invalid, revert to previous value
                        setInputValues(prev => ({ ...prev, populationRadius: formatNumber(project.config.calculation.populationRadius) }));
                        return;
                      }
                      // Clamp between 3 and 50
                      const clampedValue = Math.max(3, Math.min(50, Math.round(value)));
                      // Update the input value with the clamped value
                      setInputValues(prev => ({ ...prev, populationRadius: formatNumber(clampedValue) }));
                      // Save the clamped value to the project config
                      handleConfigChange('calculation', 'populationRadius', clampedValue);
                    }}
                    className="w-full p-2 border rounded bg-white text-[#4A4538] pr-12"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    km
                  </div>
                </div>
                {needsRecalculation && (
                  <button
                    onClick={handleRecalculateClick}
                    disabled={isRecalculating}
                    className={`px-4 py-2 border rounded bg-white text-[#4A4538] ${
                      isRecalculating
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-100 hover:border-[#6A8CAF] hover:text-[#6A8CAF]'
                    }`}
                  >
                    {isRecalculating ? 'Recalculating...' : 'Recalculate'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Import Network */}
        {project.type !== 'example' && (
          <div>
            <h2 className="text-lg font-medium text-[#4A4538] mb-4">Import Network</h2>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                Upload a GraphML file to replace the current network.
              </p>
              <div className="flex space-x-4">
                <label className="px-4 py-2 bg-white text-[#4A4538] border border-[#4A4538] rounded hover:bg-gray-100 hover:border-[#6A8CAF] hover:text-[#6A8CAF] shadow-sm text-sm transition-colors cursor-pointer">
                  Choose file
                  <input
                    type="file"
                    accept=".graphml"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                <span className="py-2 text-gray-500">
                  {selectedFileName}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Project Actions */}
        <div className="flex space-x-4">
          <button
            onClick={onDuplicateProject}
            className="px-4 py-2 bg-[#6A8CAF] text-white rounded hover:bg-[#6A8CAF]/80"
          >
            Duplicate Project
          </button>
          {project.type === 'example' ? (
            <>
              {showSkipFlag && (
                <button
                  onClick={() => {
                    resetSkipFlag();
                    setShowSkipFlag(false);
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Reset Skip Flag
                </button>
              )}
              <button
                onClick={async () => {
                  if (confirm('Are you sure you want to reset this example project to its original state?')) {
                    try {
                      const store = new ProjectStore();
                      await store.init();
                      
                      // Reset the project (this will delete the old one and create a new one)
                      const success = await ExampleProjectLoader.resetExampleProject(project.id, store);
                      if (success) {
                        // Get the most recent example project (which will be our new one)
                        const projects = await store.getActiveProjects();
                        const newProject = projects.exampleProjects[0]; // Get the first example project
                        if (newProject) {
                          const fullProject = await store.getProject(newProject.id);
                          if (fullProject) {
                            onProjectChange(fullProject);
                            alert('Example project has been reset successfully.');
                          }
                        }
                      } else {
                        alert('Failed to reset example project. Please try again.');
                      }
                    } catch (error) {
                      console.error('Failed to reset example project:', error);
                      alert('Failed to reset example project. Please try again.');
                    }
                  }
                }}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
              >
                Reset Example
              </button>
            </>
          ) : (
            <button
              onClick={handleDeleteClick}
              className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
            >
              Delete Project
            </button>
          )}
          <button
            onClick={async () => {
              const userInput = prompt('Type "delete all" to confirm deletion of all projects:');
              if (userInput?.toLowerCase() === 'delete all') {
                await onDeleteAllProjects();
              }
            }}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Delete All
          </button>
        </div>
      </div>
    </div>
  );
}; 